import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if we're on a blog post page
  const isBlogPage = location.pathname.startsWith('/blog/');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Inicio', href: '#inicio' },
    { name: 'Servicios', href: '#servicios' },
    { name: 'Nosotros', href: '#nosotros' },
    { name: 'Blog', href: '#blog' },
    { name: 'Contacto', href: '#contacto' },
  ];

  const handleNavClick = (href) => {
    setIsMobileMenuOpen(false);
    
    if (isBlogPage) {
      // If on blog page, navigate to home first then scroll after a delay
      navigate('/');
      // Use setTimeout to wait for navigation and then scroll
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // If on home page, just scroll
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (isBlogPage) {
      navigate('/');
    } else {
      const element = document.querySelector('#inicio');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Determine text color based on scroll state and page type
  const getTextColor = () => {
    return 'text-white';
  };

  const getIconColor = () => {
    return 'text-white';
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isBlogPage
          ? 'bg-[#385723] shadow-lg py-3'
          : 'bg-[#385723] py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a
            href="/"
            onClick={handleLogoClick}
            data-testid="logo-link"
            className="flex items-center"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_d969f8a4-cc05-414e-b246-ef00347927ad/artifacts/xzse4tpn_Faraci%20-01.png"
              alt="Faraci Edificaciones Logo"
              className="w-auto transition-all duration-300"
              style={{ height: '4rem' }}
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => handleNavClick(link.href)}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`nav-link font-manrope font-medium text-sm uppercase tracking-wider ${getTextColor()} hover:text-[#92D050]`}
              >
                {link.name}
              </button>
            ))}
            <Button
              onClick={() => handleNavClick('#contacto')}
              data-testid="nav-cta-button"
              className="bg-[#92D050] text-[#385723] hover:bg-[#7ab340] font-oswald uppercase tracking-wider px-6"
            >
              <Phone className="w-4 h-4 mr-2" />
              Contáctanos
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${getIconColor()}`} />
            ) : (
              <Menu className={`w-6 h-6 ${getIconColor()}`} />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden absolute top-full left-0 right-0 bg-[#385723] shadow-lg py-6"
          >
            <div className="flex flex-col items-center gap-4">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => handleNavClick(link.href)}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  className="font-manrope font-medium text-white hover:text-[#92D050] py-2"
                >
                  {link.name}
                </button>
              ))}
              <Button
                onClick={() => handleNavClick('#contacto')}
                data-testid="mobile-nav-cta-button"
                className="bg-[#92D050] text-[#385723] hover:bg-[#7ab340] font-oswald uppercase tracking-wider px-6 mt-4"
              >
                <Phone className="w-4 h-4 mr-2" />
                Contáctanos
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
